import { useEffect, useRef, useState } from 'react';
import type { WebViewerInstance } from '@pdftron/webviewer';
import { useI18n } from '../../i18n';
import { toast } from 'sonner';
import { libraryApi, type Material } from '../../services/api';
import { getLibraryState } from '../../stores/libraryStore';
import { useUserStore } from '../../stores/userStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from './ui/dialog';
import type { usePdfDocument } from './use-pdf-document';
import { createSavePdfCopyAttempt, savePdfCopy, SavePdfCopyError } from './save-pdf-copy';

const saveElement = 'sourcitySaveAnnotations';
const copyElement = 'sourcitySaveCopyToLibrary';

export function PdfLibrarySave({ instance, material, userId, getToken, annotations }: {
  instance: WebViewerInstance | null;
  material: Material | null;
  userId: string | null | undefined;
  getToken: () => Promise<string | null>;
  annotations: ReturnType<typeof usePdfDocument>;
}) {
  const { t, lang: locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState('');
  const [phase, setPhase] = useState<'idle' | 'exporting' | 'saving'>('idle');
  const [error, setError] = useState('');
  const [copy, setCopy] = useState<Material | null>(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const attempt = useRef<ReturnType<typeof createSavePdfCopyAttempt> | null>(null);
  const current = useRef({ material, userId, annotations, getToken });
  current.current = { material, userId, annotations, getToken };
  const ready = Boolean(material && !['loading', 'load-error', 'legacy-readonly', 'annotations-load-error'].includes(annotations.status));
  const actions = useRef({ openCopy: () => {}, saveChanges: () => {} });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const saveChanges = async () => {
    const source = current.current;
    if (busy.current || !source.material || ['loading', 'load-error', 'legacy-readonly', 'annotations-load-error'].includes(source.annotations.status)) return;
    try {
      await source.annotations.saveNow();
      if (mounted.current && current.current.material?.id === source.material.id && current.current.userId === source.userId) {
        toast.success(t('reader.documentSave.saved'));
      }
    } catch {
      if (mounted.current && current.current.userId === source.userId) toast.error(t('reader.documentSave.saveFailed'));
    }
  };
  actions.current = {
    saveChanges: () => { void saveChanges(); },
    openCopy: () => {
      if (!ready || busy.current || !material) return;
      // A deliberate new dialog starts a new copy; retries inside it retain the
      // exact File/session, including after an uncertain network response.
      attempt.current = null;
      setFilename(t('reader.documentSave.defaultFilename', {
        name: (material.original_filename || material.filename).replace(/\.pdf$/i, ''),
      }));
      setCopy(null);
      setError('');
      setOpen(true);
    },
  };

  useEffect(() => {
    if (!instance) return;
    // The pinned SDK uses the classic hamburger menu. MainMenuFlyout belongs
    // to the modular UI and does not render in this viewer.
    const menu = instance.UI.settingsMenuOverlay;
    if (!menu) return;
    const withoutOurs = () => menu.getItems().filter(item => ![saveElement, copyElement].includes(
      (item as { dataElement?: string }).dataElement || '',
    ));
    const items = withoutOurs();
    const index = items.findIndex(item => (item as { dataElement?: string }).dataElement === 'saveAsButton');
    const action = (callback: 'saveChanges' | 'openCopy') => () => {
      instance.UI.closeElements(['menuOverlay']);
      actions.current[callback]();
    };
    items.splice(index < 0 ? 0 : index, 0,
      { type: 'actionButton', className: 'row', dataElement: saveElement,
        label: t('reader.documentSave.saveChanges'), img: 'icon-save', onClick: action('saveChanges') },
      { type: 'actionButton', className: 'row', dataElement: copyElement,
        label: t('reader.documentSave.saveCopyTitle'), img: 'icon-save', onClick: action('openCopy') },
    );
    menu.update(items);
    return () => { menu.update(withoutOurs()); };
  }, [instance, t, locale]);

  useEffect(() => {
    if (!instance) return;
    if (ready && phase === 'idle') instance.UI.enableElements([saveElement, copyElement]);
    else instance.UI.disableElements([saveElement, copyElement]);
  }, [instance, ready, phase]);

  useEffect(() => {
    setOpen(false);
    setError('');
    setCopy(null);
    attempt.current = null;
  }, [material?.id, userId]);

  const submit = async () => {
    if (busy.current || !ready || !filename.trim() || !material || !userId) return;
    busy.current = true;
    const owner = userId;
    const sourceId = material.id;
    const sameOwner = () => mounted.current && current.current.userId === owner;
    const stillHere = () => sameOwner() && current.current.material?.id === sourceId;
    const ownerToken = async () => {
      if (!sameOwner()) throw new Error('Copy owner changed');
      const token = await current.current.getToken();
      if (!token || !sameOwner()) throw new Error('Copy authentication unavailable');
      return token;
    };
    setError('');
    try {
      let retained = attempt.current;
      if (!retained) {
        setPhase('exporting');
        const bytes = await annotations.exportPdf();
        if (!stillHere()) return;
        const name = filename.trim().replace(/[\\/\u0000-\u001f]/g, '_').replace(/\.pdf$/i, '') + '.pdf';
        retained = createSavePdfCopyAttempt(new File([bytes], name, { type: 'application/pdf' }));
        attempt.current = retained;
      }
      setPhase('saving');
      const result = await savePdfCopy(retained, {
        getToken: ownerToken,
        uploadFile: (file, token) => getLibraryState().uploadFile(file, token, sameOwner),
        pollUploadStatus: (session, tokenProvider) => getLibraryState().pollUploadStatus(session, tokenProvider, sameOwner),
        getUploadStatus: libraryApi.getUploadStatus,
        refreshAndFindMaterial: async (id, token) => {
          await getLibraryState().fetchLibrary(token, sameOwner);
          if (!sameOwner()) return null;
          return getLibraryState().materials.find(item => item.id === id) || null;
        },
      });
      if (!stillHere()) return;
      setCopy(result);
      toast.success(t('reader.documentSave.copySaved'));
      void ownerToken().then(token => useUserStore.getState().fetchLimits(token, sameOwner)).catch(() => {});
    } catch (failure) {
      if (!stillHere()) return;
      setError(t(failure instanceof SavePdfCopyError
        ? failure.code === 'pending' ? 'reader.documentSave.copyPending'
          : failure.code === 'failed' ? 'reader.documentSave.copyProcessingFailed'
            : 'reader.documentSave.copyUnconfirmed'
        : attempt.current ? 'reader.documentSave.copyFailed' : 'reader.documentSave.exportFailed'));
    } finally {
      busy.current = false;
      if (mounted.current) setPhase('idle');
    }
  };

  return <>
    {material && instance && ['unsaved', 'saving'].includes(annotations.status) && (
      <p role="status" className="shrink-0 border-b border-slate-200 bg-white px-3 py-1 text-xs leading-relaxed text-slate-600">
        {t(annotations.status === 'saving' ? 'reader.documentSave.savingChanges' : 'reader.documentSave.unsavedChanges')}
      </p>
    )}
    {material && ['legacy-readonly', 'annotations-load-error'].includes(annotations.status) && (
      <div role="alert" className="flex shrink-0 items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-950">
        <span>{t(annotations.status === 'legacy-readonly' ? 'reader.documentSave.legacyReadOnly' : 'reader.documentSave.annotationsUnavailable')}</span>
        <Button size="sm" variant="outline" className="shrink-0" onClick={annotations.retryLoad}>{t('reader.documentSave.retryLoad')}</Button>
      </div>
    )}
    {material && annotations.status === 'error' && (
      <div role="alert" className="flex shrink-0 items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-950">
        <span>{t('reader.documentSave.saveFailed')}</span>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => void saveChanges()}>{t('reader.documentSave.retrySave')}</Button>
      </div>
    )}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent dir={locale === 'ar' ? 'rtl' : 'ltr'} className="max-h-[85dvh] overflow-y-auto text-start">
        <DialogTitle className="pe-6 leading-normal">{t('reader.documentSave.saveCopyTitle')}</DialogTitle>
        <DialogDescription className="text-slate-600">{t('reader.documentSave.copyDescription')}</DialogDescription>
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-950">{t('reader.documentSave.trialWatermark')}</p>
        {copy ? (
          <p role="status" className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-900">{t('reader.documentSave.copySaved')}</p>
        ) : <form id="save-pdf-library-copy" onSubmit={event => { event.preventDefault(); void submit(); }} className="space-y-3">
          <Label htmlFor="pdf-library-copy-filename">{t('reader.documentSave.filename')}</Label>
          <Input id="pdf-library-copy-filename" dir="auto" maxLength={180} value={filename}
            disabled={phase !== 'idle' || Boolean(attempt.current)} onChange={event => setFilename(event.target.value)} />
          <p className="text-xs text-slate-600">{t('reader.documentSave.copyAllowance')}</p>
          {phase !== 'idle' && <p role="status" className="text-sm">{t(phase === 'exporting' ? 'reader.documentSave.exporting' : 'reader.documentSave.savingCopy')}</p>}
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        </form>}
        <DialogFooter>
          <Button className="min-h-11" variant="outline" onClick={() => setOpen(false)}>{t('reader.documentSave.close')}</Button>
          {copy ? <Button className="min-h-11" onClick={() => { getLibraryState().selectMaterial(copy); setOpen(false); }}>{t('reader.documentSave.openCopy')}</Button>
            : <Button className="min-h-11" form="save-pdf-library-copy" type="submit" disabled={phase !== 'idle' || !filename.trim() || !ready}>
              {t(attempt.current?.sessionId ? 'reader.documentSave.checkCopy' : 'reader.documentSave.saveCopy')}
            </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
